import {
	Component, Output, OnInit, OnChanges, SimpleChanges, OnDestroy,
	EventEmitter,
	Input
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { Observable, Subject, Subscription } from 'rxjs/Rx';
import { MessageService } from 'base';
import { MonitorService } from 'monitor';
import { IpAddress, Device } from 'DiscoveryEngine.TS/Core';
import {
	MonitorPolicy, MonitorSettings_Snmp, SnmpMonitorEntry
} from 'DiscoveryEngine.TS/Monitors';
import { List, ValueEncoder, Guid, Dictionary, ObjUtils } from 'DiscoveryEngine.TS/Utils';
import { MonitorConfigComponentBase } from '../MonitorConfigComponentBase';
import { IdentityService } from 'identity';
import { NavigationService } from 'netview';
import { DiscResultsService } from 'discovery';
import {
	GetDeviceResultDto, GetMonitorStateResultDto, MonitorStateDto, DisplayDeviceListEntryDto, GetMonitorDeviceStatesResultDto, DeviceStateDto
} from 'UvExplorerServer.Dtos.TS/Session';
import { MonitorDeviceSelectorResult } from '../monitor-device-selector/monitor-device-selector.component';
import { CustomSnmpMonitorComponentState } from '../customsnmpmonitor/customsnmpmonitor.component';


export class CustomSnmpMonitorDeviceListEntry {
	public deviceGuid: string;
	public deviceName: string;
	public deviceDescription: string;
	public ipAddress: string;
	public entryName: string;
	public entryOid: string;
	public state: string;
	public stateDetails: string;
	public style: string;

	public deviceTag: Device;
	public get hasDeviceTag(): boolean {
		return !!this.deviceTag;
	}

	public entryTag: SnmpMonitorEntry;
	public get hasEntryTag(): boolean {
		return !!this.entryTag;
	}

	public ipAddressTag: IpAddress;
	public get hasIpAddressTag(): boolean {
		return !!this.ipAddressTag;
	}

	constructor(deviceGuid?: string, device?: Device, entry?: SnmpMonitorEntry, deviceState?: string, deviceStateDetails?: string) {
		if (deviceGuid) {
			this.deviceGuid = deviceGuid;

			if (device) {
				this.deviceName = device.DisplayName;
				this.deviceDescription = device.DisplayDescription;
			}
			else {
				this.deviceName = deviceGuid;
				this.deviceDescription = "";
			}

			if (device.HasIpAddress) {
				this.ipAddress = device.IpAddress.ToString();
				this.ipAddressTag = device.IpAddress;
			}
			else {
				this.ipAddress = "";
				this.ipAddressTag = null;
			}

			if (entry) {
				this.entryName = entry.Name;
				this.entryOid = entry.Oid;
			}
			else {
				this.entryName = "";
				this.entryOid = "";
			}

			if (deviceState) {
				this.state = deviceState;
				if (this.state === "NoData") {
					this.state = "No Data";
				}

				this.style = "monitor-state-" + deviceState.toLowerCase();
			}
			else {
				this.state = "No Data";
				this.style = "monitor-state-nodata";
			}

			if (deviceStateDetails) {
				this.stateDetails = deviceStateDetails;
			}
			else {
				this.stateDetails = "";
			}

			this.deviceTag = device;
			this.entryTag = entry;
		}
		else {
			this.deviceGuid = "";
			this.deviceName = "";
			this.deviceDescription = "";
			this.ipAddress = "";
			this.entryName = "";
			this.entryOid = "";
			this.state = "No Data";
			this.style = "monitor-state-nodata";
			this.stateDetails = "";

			this.deviceTag = null;
			this.entryTag = null;
			this.ipAddressTag = null;
		}
	}
}

@Component({
	selector: 'custom-snmp-devices',
	templateUrl: './customsnmpdevices.component.html',
	styleUrls: ['./customsnmpdevices.component.scss']
})
export class CustomSnmpDevicesComponent extends MonitorConfigComponentBase implements OnInit, OnChanges, OnDestroy {

	@Input()
	public monEditorState: CustomSnmpMonitorComponentState;

	private monPolicy: MonitorPolicy;
	private monSettings: MonitorSettings_Snmp;

	public entries: CustomSnmpMonitorDeviceListEntry[];
	public deviceSelectorVisible: boolean;

	public UseHostnames: UntypedFormControl;

	private settingsChangeSub: Subscription;

	constructor(private monService: MonitorService,
		private messageService: MessageService,
		private identityService: IdentityService,
		private navService: NavigationService,
		private discResultsService: DiscResultsService,
		private fb: UntypedFormBuilder) {

		super();

		this.monEditorState = null;

		this.monPolicy = null;
		this.monSettings = null;

		this.entries = [];
		this.deviceSelectorVisible = false;

		this.UseHostnames = null;

		this.settingsChangeSub = null;
	}

	private init(): void {
		this.monPolicy = null;
		this.monSettings = null;

		if (this.editor) {
			this.monPolicy = this.editor.Policy;
			this.monSettings = <MonitorSettings_Snmp>this.monPolicy.Settings;
		}
	}

	public ngOnInit(): void {
		this.init();
		this.BuildForm();
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.init();
		this.entries = [];
		this.BuildForm();

		this.unsubscribeAllEvents();

		if (this.monEditorState) {
			this.settingsChangeSub = this.monEditorState.settingsChange.subscribe(
				(result: boolean) => {
					if (result) {
						this.rebuildEntries();
					}
				}
			);
		}

		if (this.visible) {
			this.retrieveDevicesAndDeviceStates();
		}
	}

	public ngOnDestroy(): void {
		this.unsubscribeAllEvents();
	}

	private unsubscribeAllEvents(): void {
		if (this.settingsChangeSub) {
			this.settingsChangeSub.unsubscribe();
			this.settingsChangeSub = null;
		}
	}

	private retrieveDevicesAndDeviceStates(): void {

		if (this.editor) {
			if (this.editor.Policy.DeviceGuids.Count > 0) {
				let deviceGuidStrs = new List<string>();
				this.editor.Policy.DeviceGuids.ForEach((guid: Guid) => {
					let guidStr = ValueEncoder.EncodeGuid(guid);
					deviceGuidStrs.Add(guidStr);
				});

				this.getDevices(deviceGuidStrs).subscribe(
					(gdResult: any) => {
						this.getMonitorState(this.monPolicy, false).subscribe(
							(gmsResult: any) => {
								this.rebuildEntries();
							},
							(gmsError) => {
								this.rebuildEntries();
								this.messageService.displayErrorMessage("Could not retrieve monitor state info");
							});
					},
					(gdError) => {
						this.messageService.displayErrorMessage("Could not retrieve monitor device info");
					});
			}
			else {
				this.rebuildEntries();
			}
		}
	}

	private stateKey(entryGuid: string | Guid, deviceGuid: string | Guid): string {

		return (ObjUtils.GuidToString(entryGuid) + ":" + ObjUtils.GuidToString(deviceGuid));
	}

	private rebuildEntries(): void {

		this.entries = [];

		if (this.editor) {
			this.monPolicy.DeviceGuids.ForEach((deviceGuid: Guid) => {

				let deviceGuidStr = ObjUtils.GuidToString(deviceGuid);

				let device: Device = null;
				if (this.monEditorState.deviceCache[deviceGuidStr]) {
					device = this.monEditorState.deviceCache[deviceGuidStr];
				}

				if (device) {
					if (this.monSettings.Count > 0) {
						this.monSettings.GetEntries().ForEach((entry: SnmpMonitorEntry) => {
							if (entry.DeviceGuid.Equals(device.GUID)) {
								let state: string = "";
								let stateDetails: string = "";

								let key = this.stateKey(entry.Guid, entry.DeviceGuid);
								if (this.monEditorState.stateCache[key]) {
									let entryState: MonitorStateDto = this.monEditorState.stateCache[key];
									state = entryState.monitorSettingsEntryState;
									stateDetails = entryState.monitorSettingsEntryStateDetails;
								}

								let listEntry = new CustomSnmpMonitorDeviceListEntry(deviceGuidStr, device, entry, state, stateDetails);
								this.entries.push(listEntry);
							}
						});
					}
					else {
						let listEntry = new CustomSnmpMonitorDeviceListEntry(deviceGuidStr, device, null, null, null);
						this.entries.push(listEntry);
					}
				}
			});
		}
	}

	private getDevices(deviceGuids: List<string>): Observable<any> {

		let subject = new Subject<any>();

		let sessionGuid = this.identityService.sessionGuid;
		let netViewGuid = this.navService.location.netViewGuid;
		let agentGuid = this.navService.location.agentGuid;
		let schedDiscGuid = this.navService.location.schedDiscGuid;
		let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

		this.discResultsService.getDevice(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
			schedDiscRsltGuid, "monitor-network", deviceGuids, false).subscribe(
				(result: GetDeviceResultDto) => {
					this.monEditorState.deviceCache = {};
					result.Devices.ForEach((value: Device) => {
						let deviceGuidStr = ValueEncoder.EncodeGuid(value.GUID);
						this.monEditorState.deviceCache[deviceGuidStr] = value;
					});

					this.monEditorState.deviceCacheChange.emit(true);
					subject.next(true);
				},
				(error) => {
					this.monEditorState.deviceCacheChange.emit(false);
					subject.error(false);
				});

		return subject.asObservable();
	}

	private getMonitorState(policy: MonitorPolicy, showBusy: boolean): Observable<any> {

		let subject = new Subject<any>();

		let sessionGuid = this.identityService.sessionGuid;
		let netViewGuid = this.navService.location.netViewGuid;
		let agentGuid = this.navService.location.agentGuid;
		let schedDiscGuid = this.navService.location.schedDiscGuid;
		let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

		this.monService.getMonitorState(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
			schedDiscRsltGuid, policy, showBusy).subscribe(
				(result: GetMonitorStateResultDto) => {
					this.monEditorState.stateCache = {};
					result.entries.forEach((entry: MonitorStateDto) => {
						let key = this.stateKey(entry.monitorSettingsEntryGuid, entry.deviceGuid);
						this.monEditorState.stateCache[key] = entry;
					});

					this.monEditorState.stateCacheChange.emit(true);
					subject.next(true);
				},
				(error) => {
					this.monEditorState.stateCacheChange.emit(false);
					subject.error(false);
				});

		return subject.asObservable();
	}

	public BuildForm(): void {
		let useHostnames = false;
		if (this.editor) {
			let monPolicy: MonitorPolicy = this.editor.Policy;
			let monSettings: MonitorSettings_Snmp = <MonitorSettings_Snmp>monPolicy.Settings;

			useHostnames = monSettings.PollByHostname;
		}

		this.UseHostnames = new UntypedFormControl(useHostnames);

		this.SettingsForm = this.fb.group({
			UseHostnames: this.UseHostnames,
		});
	}

	public applyTempChanges(): void {
		if (!!this.editor && this.editor.HasPolicy) {

			let monPolicy: MonitorPolicy = this.editor.Policy;
			let monSettings: MonitorSettings_Snmp = <MonitorSettings_Snmp>monPolicy.Settings;

			monSettings.PollByHostname = this.UseHostnames.value;
			this.editor.HasChanges = true;

			this.BuildForm();
		}
	}

	public get invalidTempChanges(): string[] {
		return ["Devices"];
	}

	public addDevice(): void {
		this.deviceSelectorVisible = true;
	}

	public deleteDevice(listEntry: CustomSnmpMonitorDeviceListEntry): void {

		let device = this.monEditorState.deviceCache[listEntry.deviceGuid];

		if (device) {
			this.editor.RemoveSnmpMonitorDevice(device);
			this.rebuildEntries();
		}
	}

	public refreshEntries(): void {
		this.retrieveDevicesAndDeviceStates();
	}

	public onDeviceSelectorOk(result: MonitorDeviceSelectorResult): void {

		let devicesToAdd = new List<string>();
		result.devices.forEach((value: DisplayDeviceListEntryDto) => {
			let deviceGuid = Guid.FromString(value.deviceGuid);
			if (!this.editor.Policy.DeviceGuids.Contains(deviceGuid)) {
				devicesToAdd.Add(value.deviceGuid);
			}
		});

		if (devicesToAdd.Count > 0) {
			this.getDevices(devicesToAdd).subscribe(
				() => {
					let addedDevices = new List<string>();

					devicesToAdd.ForEach((deviceGuidStr: string) => {
						let device: Device = this.monEditorState.deviceCache[deviceGuidStr];
						if (!!device) {
							this.editor.AddSnmpMonitorDevice(device);
							addedDevices.Add(deviceGuidStr);
						}
					});

					if (addedDevices.Count > 0) {
						// Make sure new monitor entries have GUIDs
						this.editor.Policy.VerifyGuids();
						this.retrieveDevicesAndDeviceStates();
					}

					if (addedDevices.Count < devicesToAdd.Count) {
						this.messageService.displayErrorMessage("Error adding device(s)");
					}
				},
				() => {
					this.messageService.displayErrorMessage("Could not retrieve monitor device info");
				});
		}
		else {
			this.messageService.displayInfoMessage("No new devices were added");
		}
	}

	public onDeviceSelectorCancel(): void {
		return;
	}

	public doCustomSort(event): void {
		let comparer = function (a: CustomSnmpMonitorDeviceListEntry, b: CustomSnmpMonitorDeviceListEntry): number {
			let result = 0;

			switch (event.field) {
				case "ipAddress":
					if (a.ipAddressTag && b.ipAddressTag) {
						result = a.ipAddressTag.CompareTo(b.ipAddressTag);
					}
					else if (a.ipAddressTag) {
						result = 1;
					}
					else if (b.ipAddressTag) {
						result = -1;
					}
					break;
				default:
					// Compare string fields
					result = a[event.field].toString().localeCompare(b[event.field].toString());
					break;
			}

			return result * event.order;
		}

		this.entries.sort(comparer);
	}

}
